import {useCallback} from "react";

import {AudioCache} from "@khan/guide-core-helpers";
import {GuideStateApi} from "@khan/guide-state";

import {useGuideAudioController} from "../hooks/use-guide-audio-controller.ts";
import {useStopResponseMutation} from "../queries/use-stop-response-mutation.ts";

import {STREAM_AUDIO_KEY} from "./use-guide-audio-stream.ts";

interface StopGuideResponseFn {
    /**
     * Stop the current guide response.
     *
     * This is a no-op if there is no current guide response to be stopped.
     *
     * @returns A promise that resolves when the response has been stopped.
     */
    (): Promise<void>;
}

/**
 * Obtain a function to stop the current guide response.
 *
 * @returns A function to stop the current guide response.
 */
export const useStopGuideResponse = (): StopGuideResponseFn => {
    const stopResponseMutation = useStopResponseMutation();
    const {
        audioKey,
        setAudioCache,
        stop: stopAudio,
    } = useGuideAudioController();

    const stopResponseFn = useCallback(async () => {
        const stopId = GuideStateApi.latest().stopId;
        // If stop playing the audio if the audioKey is the streaming audio key
        // and clear the streaming audio buffer audio.
        if (audioKey === STREAM_AUDIO_KEY) {
            stopAudio();
        }
        setAudioCache((prev) => {
            const nextAudioCache = new AudioCache(prev);
            nextAudioCache.set(STREAM_AUDIO_KEY, []);
            return nextAudioCache;
        });
        if (stopId != null) {
            await stopResponseMutation(stopId);
            GuideStateApi.set("stopId", null);
        }
    }, [stopResponseMutation, audioKey, setAudioCache, stopAudio]);

    return stopResponseFn;
};
